<script setup lang="ts">
import ProgressFrame from '@/modules/commissioning/views/layout/ProgressFrame.vue';
import { useCommissioningDeviceStore } from '@/modules/commissioning/stores/commissioningDeviceStore';
import type { CommissionResult } from '@/modules/commissioning/models/CommissioningDevice';
import { getCommissionResultConfigValues } from '@/modules/commissioning/utils/error-resolver';
import type { ComponentErrorType } from '@/modules/commissioning/utils/error-constants';
import ComErrorIds from '@/modules/commissioning/components/ComErrorIds.vue';
import ComErrorCtaButtons from '@/modules/commissioning/components/ComErrorCtaButtons.vue';
import { ERROR_CTA_BUTTONS } from '@/modules/commissioning/utils/error-constants';

const errorType = 'CTNotReading' as ComponentErrorType;
const props = defineProps<{
  failIdsFromRoute?: string[]; // For debugging purposes only
}>();

const deviceStore = useCommissioningDeviceStore();
const commissionResult = deviceStore.commissionResultStatus.results ?? ({} as CommissionResult);

/**
 * Get the fail IDs from the commission result
 */
const commissionResultFailIds = getCommissionResultConfigValues(commissionResult, 'failId');
const failIds = props.failIdsFromRoute ?? commissionResultFailIds[errorType] ?? [];

/**
 * Get the CTA buttons from the commission result
 */
const commissionResultCtaButtons = getCommissionResultConfigValues(commissionResult, 'ctaButtons');
const ctaButtons = commissionResultCtaButtons[errorType] ?? [
  ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
  ERROR_CTA_BUTTONS.RECONFIGURE,
];
</script>

<template>
  <ProgressFrame
    pageClass="fullfocus-page"
    titleTight
    withBackArrow
    backButtonClass="error-back-button"
  >
    <template #title><div class="error-title">CT not reading</div></template>
    <template #subTitle
      ><div class="error-sub-title">How to troubleshoot a CT that is not reading.</div></template
    >

    <div v-if="failIds.length > 0" class="error-fail-ids--wrapper">
      <ComErrorIds :failIds="failIds" unique />
    </div>

    <div class="error-intro">
      <p>
        <strong>Each CT must be</strong>
      </p>
      <ul class="error-bullet-list">
        <li>Clipped around the correct cable</li>
        <li>Clipped in the correct orientation</li>
        <li>Correctly fitted to the SolShare connector</li>
        <li>If a CT has been extended, a good electrical contact at the point of connection.</li>
      </ul>
      <p>
        You need to <strong>check both ends</strong> of the connection and any
        <strong>CT extensions</strong> that may be used in the installation.
      </p>
    </div>

    <div class="error-handle-section">
      <h4>CT Install End</h4>
      <p>Check that the CT is:</p>
      <ul class="error-bullet-list">
        <li>Clipped around the correct cable for that connection</li>
        <li>Clipped on the live meter tail <b>before</b> the solar connection (grid side)</li>
        <li>Ensure the arrow (on the base of the CT) is pointing towards the load.</li>
      </ul>
    </div>

    <div class="error-handle-section">
      <h4>CT Extensions</h4>
      <p>Check the connection at the point of extension.</p>
    </div>

    <div class="error-handle-section">
      <h4>SolShare End</h4>
      <p>CT connector check the CT cables are fully inserted and secure in the terminal block.</p>
    </div>

    <ComErrorCtaButtons :ctaButtons="ctaButtons" />
  </ProgressFrame>
</template>

<style lang="less" scoped></style>
